"use client"

export const dynamic = 'force-dynamic'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Calendar, Plus, Bug, Zap, Shield, Globe } from "lucide-react"
import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"

export default function ChangelogPage() {
  const releases = [
    {
      version: "v2.4.0",
      date: "December 18, 2025",
      type: "major",
      changes: [
        { type: "feature", text: "Added support for YAML file formats" },
        { type: "feature", text: "New batch translation API endpoint" },
        { type: "feature", text: "Enhanced context awareness for technical translations" },
        { type: "improvement", text: "50% faster file processing for large documents" },
        { type: "fix", text: "Fixed issue with nested object translation ordering" }
      ]
    },
    {
      version: "v2.3.2",
      date: "December 10, 2025",
      type: "patch",
      changes: [
        { type: "fix", text: "Resolved timeout issues for files larger than 10MB" },
        { type: "fix", text: "Fixed API key validation error messages" },
        { type: "improvement", text: "Improved error handling for OpenAI API rate limits" }
      ]
    },
    {
      version: "v2.3.1",
      date: "December 5, 2025",
      type: "patch",
      changes: [
        { type: "fix", text: "Fixed dashboard loading issue for users with many projects" },
        { type: "security", text: "Enhanced API key encryption with additional security layer" }
      ]
    },
    {
      version: "v2.3.0",
      date: "November 28, 2025",
      type: "minor",
      changes: [
        { type: "feature", text: "Added team collaboration features" },
        { type: "feature", text: "New project templates for common frameworks" },
        { type: "feature", text: "CLI tool now supports automated deployment hooks" },
        { type: "improvement", text: "Enhanced translation quality for context-specific terms" },
        { type: "improvement", text: "Better handling of pluralization rules" }
      ]
    },
    {
      version: "v2.2.1",
      date: "November 20, 2025",
      type: "patch",
      changes: [
        { type: "fix", text: "Fixed file upload progress indicator" },
        { type: "fix", text: "Resolved issue with special characters in file names" },
        { type: "improvement", text: "Optimized memory usage for large file processing" }
      ]
    },
    {
      version: "v2.2.0",
      date: "November 15, 2025",
      type: "minor",
      changes: [
        { type: "feature", text: "Added webhook notifications for translation completion" },
        { type: "feature", text: "New API endpoint for translation status monitoring" },
        { type: "feature", text: "Support for custom glossaries and terminology" },
        { type: "improvement", text: "Enhanced dashboard with usage analytics" },
        { type: "fix", text: "Fixed edge case with empty translation strings" }
      ]
    },
    {
      version: "v2.1.3",
      date: "November 8, 2025",
      type: "patch",
      changes: [
        { type: "security", text: "Updated dependencies to address security vulnerabilities" },
        { type: "fix", text: "Fixed issue with translation caching" },
        { type: "improvement", text: "Reduced API response times by 15%" }
      ]
    },
    {
      version: "v2.1.2",
      date: "November 1, 2025",
      type: "patch",
      changes: [
        { type: "fix", text: "Fixed authentication issues with third-party OAuth providers" },
        { type: "fix", text: "Resolved file download issues in Safari browser" }
      ]
    },
    {
      version: "v2.1.1",
      date: "October 25, 2025",
      type: "patch",
      changes: [
        { type: "fix", text: "Fixed issue with concurrent translation requests" },
        { type: "improvement", text: "Better error messages for invalid API keys" }
      ]
    },
    {
      version: "v2.1.0",
      date: "October 20, 2025",
      type: "minor",
      changes: [
        { type: "feature", text: "Added CLI tool for automated workflows" },
        { type: "feature", text: "New dark mode for dashboard interface" },
        { type: "feature", text: "Support for translation history and version control" },
        { type: "improvement", text: "Enhanced file validation and error reporting" },
        { type: "improvement", text: "Better mobile responsiveness for dashboard" }
      ]
    }
  ]

  const getChangeIcon = (type: string) => {
    switch (type) {
      case "feature":
        return <Plus className="h-4 w-4 text-green-600" />
      case "fix":
        return <Bug className="h-4 w-4 text-red-600" />
      case "improvement":
        return <Zap className="h-4 w-4 text-blue-600" />
      case "security":
        return <Shield className="h-4 w-4 text-purple-600" />
      default:
        return <Globe className="h-4 w-4 text-gray-600" />
    }
  }

  const getVersionBadge = (type: string) => {
    switch (type) {
      case "major":
        return <Badge className="bg-green-600 hover:bg-green-700">Major Release</Badge>
      case "minor":
        return <Badge variant="secondary">Minor Release</Badge>
      case "patch":
        return <Badge variant="outline">Patch</Badge>
      default:
        return <Badge variant="outline">Release</Badge>
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <Navigation />

      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
              Changelog
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Stay up to date with new features, improvements, and bug fixes in Locsy
            </p>
          </div>

          {/* Legend */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle>Legend</CardTitle>
              <CardDescription>Understanding the different types of changes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  <Plus className="h-4 w-4 text-green-600" />
                  <span className="text-sm">New Feature</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-blue-600" />
                  <span className="text-sm">Improvement</span>
                </div>
                <div className="flex items-center gap-2">
                  <Bug className="h-4 w-4 text-red-600" />
                  <span className="text-sm">Bug Fix</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-purple-600" />
                  <span className="text-sm">Security</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Latest Release Highlight */}
          <Card className="mb-12 border-l-4 border-l-green-500">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <span>{releases[0].version}</span>
                    {getVersionBadge(releases[0].type)}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-2 mt-2">
                    <Calendar className="h-4 w-4" />
                    {releases[0].date}
                  </CardDescription>
                </div>
                <Badge variant="secondary">Latest</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {releases[0].changes.map((change, index) => (
                  <div key={index} className="flex items-start gap-3">
                    {getChangeIcon(change.type)}
                    <span className="text-sm">{change.text}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Release History */}
          <div className="space-y-8">
            <h2 className="text-2xl font-bold">Release History</h2>
            {releases.slice(1).map((release, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <span>{release.version}</span>
                        {getVersionBadge(release.type)}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-2">
                        <Calendar className="h-4 w-4" />
                        {release.date}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {release.changes.map((change, changeIndex) => (
                      <div key={changeIndex} className="flex items-start gap-3">
                        {getChangeIcon(change.type)}
                        <span className="text-sm text-muted-foreground">{change.text}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Roadmap Teaser */}
          <Card className="mt-8">
            <CardHeader className="text-center">
              <CardTitle>Coming Soon</CardTitle>
              <CardDescription>What we're working on next</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Plus className="h-4 w-4 text-green-600 mt-0.5" />
                  <span className="text-sm text-muted-foreground">Support for additional file formats (CSV, XML, gettext)</span>
                </div>
                <div className="flex items-start gap-3">
                  <Plus className="h-4 w-4 text-green-600 mt-0.5" />
                  <span className="text-sm text-muted-foreground">Advanced translation memory and consistency checking</span>
                </div>
                <div className="flex items-start gap-3">
                  <Plus className="h-4 w-4 text-green-600 mt-0.5" />
                  <span className="text-sm text-muted-foreground">Enhanced collaboration features with real-time editing</span>
                </div>
              </div>
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Have a feature request? <a href="/contact" className="text-blue-600 hover:underline">Let us know!</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  )
} 